// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo, useState } from 'react';

import type { ItemData } from '@aps_sdk/data-management';
import type { SpoolWithModel, Project } from '@evolvemep/api-ts';
import { Flex, Loader, Stack, InputLabel, type CardProps, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AutodeskAuthWrapper } from 'components/Autodesk/auth/AutodeskAuthWrapper';
import { useAutodeskAuth } from 'components/Autodesk/auth/useAutodeskAuth';
import { FileCard } from 'components/Mantine/Attachments/AttachmentCard';
import { useMantineNotifications } from 'components/Mantine/useMantineNotifications';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useUniformFlex } from 'hooks/useUniformFlex';
import { useAutodeskModels } from 'modules/Shop/ModelBrowser/useAutodeskModels';

type ModelCardProps = {
  model: ItemData;
  isSpool?: boolean;
  onUrnSelected: (urn: string) => void;
  selectedUrn?: string;
};

const getUrn = (model: ItemData) => {
  const tipData = model.relationships.tip.data;
  if (tipData?.type === 'versions' && isNotNil(tipData.id)) {
    return tipData.id;
  }
  return undefined;
};

const ModelCard = ({ model, isSpool, selectedUrn, onUrnSelected, ...itemProps }: ModelCardProps & CardProps) => {
  const urn = useMemo(() => getUrn(model), [model]);
  return (
    <FileCard
      icon={<EvolveIcon icon={isSpool ? 'Spools' : 'ModelBrowser'} color="white" />}
      iconBg={isSpool ? 'teal.8' : 'teal.5'}
      selected={selectedUrn === urn}
      subtitle={isSpool ? 'Spool Model' : undefined}
      {...itemProps}
    >
      <Stack gap={0}>
        <Flex
          align="center"
          gap="md"
          className="attachment-link link"
          onClick={() => {
            if (urn) onUrnSelected(urn);
          }}
        >
          {model.attributes.displayName}
        </Flex>
      </Stack>
    </FileCard>
  );
};

type Props = {
  spool: SpoolWithModel | undefined;
  loadingSpool?: boolean;
  project: Pick<Project, 'projectId' | 'projectName'>;
  selectedUrn?: string;
  onUrnSelected: (urn: string, isSpoolModel?: boolean) => void;
};

const ModelSelectorWrapped = ({ spool, loadingSpool, project, onUrnSelected, selectedUrn }: Props) => {
  const { showError } = useMantineNotifications();
  const { dataClient } = useAutodeskAuth();
  const { models, modelCache, loading } = useAutodeskModels(project.projectId, { hideInaccessible: true });

  const [spoolModel, setSpoolModel] = useState<ItemData>();
  const [loadingSpoolModel, setLoadingSpoolModel] = useState(loadingSpool);
  useEffect(() => {
    (async () => {
      try {
        if (isNotNil(spool)) {
          // `spool.model` can be missing if the Spool was sent to Shop before Cloud Spooling went live
          if (isNotNil(spool.model)) {
            setLoadingSpoolModel(true);
            await dataClient
              .getItem(spool.model.autodeskProjectId, spool.model.urn)
              .then((res) => {
                if (isNil(res.data)) throw new Error('Could not load spool.');

                setSpoolModel(res.data);
              })
              .catch(showError);
          } else {
            notifications.show({
              title: 'Missing model',
              message: 'The model associated with this spool seems to be missing. Please resend the spool via Revit.',
              color: 'yellow',
              autoClose: false,
            });
          }
        }
      } finally {
        setLoadingSpoolModel(false);
      }
    })();
  }, [dataClient, showError, spool]);

  const { containerProps, itemProps } = useUniformFlex(300, 450);

  const remainingModels = useMemo(() => models?.filter((m) => m.urn !== spool?.model?.urn), [models, spool]);

  const spoolCloudModelUrn = spool?.model?.urn;
  return (
    <Flex {...containerProps} gap="xs">
      {isNotNil(spoolCloudModelUrn) && isNotNil(spoolModel) && (
        <ModelCard
          model={spoolModel}
          onUrnSelected={(urn) => onUrnSelected(urn, true)}
          selectedUrn={selectedUrn}
          isSpool
          {...itemProps}
        />
      )}

      {!loadingSpool &&
        !loadingSpoolModel &&
        remainingModels?.map((m) => (
          <ModelCard
            key={m.urn}
            // `modelCache` is guaranteed to have each model in `models` in it
            // oxlint-disable-next-line typescript/no-non-null-assertion
            model={modelCache[m.urn]!}
            onUrnSelected={(urn) => onUrnSelected(urn, false)}
            selectedUrn={selectedUrn}
            {...itemProps}
          />
        ))}

      {loadingSpool || loadingSpoolModel || loading ? (
        <Loader type="dots" m="xs" />
      ) : (
        isNil(spoolModel) &&
        models?.length === 0 && (
          <FileCard icon={null} py={4} mih={0} {...itemProps}>
            <Text c="dimmed" fz="xs">
              No models found.
            </Text>
          </FileCard>
        )
      )}
    </Flex>
  );
};

export const ModelSelector = (props: Props) => (
  <Stack gap={4}>
    <InputLabel>Linked Models</InputLabel>
    <AutodeskAuthWrapper authRequired={false}>
      <ModelSelectorWrapped {...props} />
    </AutodeskAuthWrapper>
  </Stack>
);
